import React, { useState } from "react";
import { Box, Avatar, Button, Skeleton, IconButton, Tooltip } from "@mui/material";
import { useNavigate, useMatch } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  IconPlus,
  IconSettings,
  IconLogout,
  IconBuilding,
  IconUsersGroup,
  IconArrowBackUp,
  IconBriefcase,
  IconUsers,
} from "@tabler/icons-react";
import { setUiPreference, UI_LEGACY } from "../../../utils/uiPreference";
import { paths } from "../../../routes/paths";
import { selectJob } from "../../../store/reducers/appreducer";
import { useConsole, jobIdOf } from "../../pages/Console/ConsoleContext";
import JobPanel from "../../pages/Console/JobPanel";
import tokens from "../../theme/tokens";
import { useRole } from "../../hooks/useRole";

const { color, font, layout, radius } = tokens;

const JobsRail = ({ user, userId, onEditProfile, onNavigate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // useParams() would be empty here: the rail lives in ConsoleLayout, which is the
  // element of /dashboard/jobs — the :jobId segment is matched by the child route, so
  // it isn't in this level's params. useMatch reads the full matched path instead.
  const activeJobId = useMatch(paths.v1.jobConsole)?.params?.jobId;
  const { jobs, jobsLoading, jobStats, reloadJobs } = useConsole();
  const [createOpen, setCreateOpen] = useState(false);

  const { isSuperAdmin, isCompanyAdmin } = useRole();

  const handleSelect = (job) => {
    // The URL is the source of truth for the console, but legacy pages (Application.js)
    // still read selectedJob from Redux — keep both in sync until those pages migrate.
    // selectJob also clears selectedCandidates, which is the behaviour we want on switch.
    dispatch(selectJob(job));
    navigate(`${paths.v1.jobs}/${jobIdOf(job)}`);
    onNavigate?.(); // closes the sheet on mobile
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("is_admin");
    sessionStorage.clear();
    dispatch(selectJob(null));
    navigate(paths.v1.signin, { replace: true });
  };

  const handleJobCreated = async () => {
    setCreateOpen(false);
    await reloadJobs();
  };

  const navRowSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1.75,
    py: 1,
    cursor: "pointer",
    font: `500 12px ${font.sans}`,
    color: color.textSecondary,
    "&:hover": { bgcolor: "#FAFAFC" },
  };

  return (
    <Box
      component="nav"
      sx={{
        width: layout.rail,
        flex: "none",
        bgcolor: color.surface,
        borderRight: `1px solid ${color.border}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 1.75,
          px: 1.75,
          pb: 1.25,
        }}
      >
        <Box
          component="span"
          sx={{
            font: `600 11px ${font.sans}`,
            color: color.textMuted,
            letterSpacing: ".07em",
          }}
        >
          OPEN JOBS · {jobs.length}
        </Box>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          startIcon={<IconPlus size={13} stroke={2} />}
          sx={{
            minWidth: 0,
            px: 1.125,
            py: 0.5,
            borderRadius: `${radius.button}px`,
            font: `600 12px ${font.sans}`,
            "& .MuiButton-startIcon": { mr: 0.375 },
          }}
        >
          New
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {jobsLoading &&
          Array.from({ length: 5 }).map((_, i) => (
            <Box key={i} sx={{ px: 1.75, py: 1.25 }}>
              <Skeleton width="70%" height={14} />
              <Skeleton width="45%" height={12} />
            </Box>
          ))}

        {!jobsLoading && !jobs.length && (
          <Box sx={{ px: 1.75, py: 2, font: `400 12px ${font.sans}`, color: color.textSecondary }}>
            No open jobs yet.
          </Box>
        )}

        {jobs.map((job) => {
          const id = jobIdOf(job);
          const active = String(id) === String(activeJobId);
          const stats = jobStats[id];
          return (
            <Box
              key={id}
              onClick={() => handleSelect(job)}
              sx={{
                position: "relative",
                px: 1.75,
                py: 1.25,
                cursor: "pointer",
                bgcolor: active ? color.rowTint : "transparent",
                "&:hover": { bgcolor: active ? color.rowTint : "#FAFAFC" },
                "&::before": active
                  ? {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: color.indigo,
                    }
                  : undefined,
              }}
            >
              <Box sx={{ font: `600 12.5px ${font.sans}`, color: color.text }}>
                {job["Job Title"] || "Untitled job"}
              </Box>
              <Box sx={{ mt: 0.25, font: `400 11px ${font.sans}`, color: color.textMuted }}>
                {stats ? (
                  `${stats.candidates} candidate${stats.candidates === 1 ? "" : "s"}${
                    stats.shortlist ? ` · ${stats.shortlist} in shortlist` : ""
                  }`
                ) : (
                  <Skeleton width={110} height={11} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {(isSuperAdmin || isCompanyAdmin) && (
        <Box sx={{ borderTop: `1px solid ${color.border}`, py: 0.5 }}>
          {isSuperAdmin && (
            <>
              <Box sx={navRowSx} onClick={() => navigate(paths.v1.allJobs)}>
                <IconBriefcase size={16} stroke={1.75} />
                All Jobs
              </Box>
              <Box sx={navRowSx} onClick={() => navigate(paths.v1.allUsers)}>
                <IconUsers size={16} stroke={1.75} />
                All Users
              </Box>
              <Box sx={navRowSx} onClick={() => navigate(paths.v1.companies)}>
                <IconBuilding size={16} stroke={1.75} />
                Companies
              </Box>
            </>
          )}
          {isCompanyAdmin && (
            <Box sx={navRowSx} onClick={() => navigate(paths.v1.companyUsers)}>
              <IconUsersGroup size={16} stroke={1.75} />
              Company Configuration
            </Box>
          )}
        </Box>
      )}

      {/* One click back to the old UI. Opting in has to be reversible — otherwise a bug in
          here could strand someone mid-hire. Uses a full page load, not navigate(), because
          the two UIs have separate theme roots. */}
      <Box sx={{ borderTop: `1px solid ${color.border}`, py: 0.5 }}>
        <Box
          sx={navRowSx}
          onClick={() => {
            setUiPreference(UI_LEGACY);
            window.location.href = paths.dashboard.jobs;
          }}
        >
          <IconArrowBackUp size={16} stroke={1.75} />
          Switch to old version
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${color.border}`,
          px: 1.75,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: color.indigoTint,
            color: color.indigo,
            font: `600 11px ${font.sans}`,
          }}
        >
          {[user?.firstName, user?.lastName]
            .filter(Boolean)
            .map((n) => n[0])
            .join("")
            .toUpperCase() || "?"}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              font: `600 12px ${font.sans}`,
              color: color.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Account"}
          </Box>
          <Box sx={{ font: `400 10.5px ${font.sans}`, color: color.textMuted }}>
            {isSuperAdmin ? "Super Admin" : isCompanyAdmin ? "Admin" : "HR Manager"}
          </Box>
        </Box>
        <Tooltip title="Edit profile">
          <IconButton size="small" onClick={onEditProfile}>
            <IconSettings size={16} stroke={1.75} color={color.textMuted} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Log out">
          <IconButton size="small" onClick={handleLogout}>
            <IconLogout size={16} stroke={1.75} color={color.textMuted} />
          </IconButton>
        </Tooltip>
      </Box>

      <JobPanel
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleJobCreated}
      />
    </Box>
  );
};

export default JobsRail;
